/** Question types — aligned with bulk upload + editor. */

export const QUESTION_TYPES = [
  {
    value: "rating",
    label: "Star rating (1–5)",
    needsOptions: false,
    scorable: true,
  },
  {
    value: "importance_agreement",
    label: "Importance × Agreement",
    needsOptions: false,
    scorable: true,
  },
  { value: "open_ended", label: "Open ended", needsOptions: false, scorable: false },
  { value: "dropdown", label: "Dropdown", needsOptions: true, scorable: true },
  { value: "single_choice", label: "Single choice", needsOptions: true, scorable: true },
  { value: "multiple_choice", label: "Multiple choice", needsOptions: true, scorable: true },
];

export const STAR_FORMULAS = [
  {
    value: "star_value",
    label: "Star value (1–5)",
    hint: "Score is the number of stars the participant selects.",
  },
];

export const IMPORTANCE_AGREEMENT_FORMULAS = [
  {
    value: "importance_x_agreement",
    label: "Importance × Agreement",
    hint: "Score = importance weight × agreement weight.",
  },
  {
    value: "agreement_only",
    label: "Agreement only",
    hint: "Use only the agreement scale.",
  },
  {
    value: "importance_only",
    label: "Importance only",
    hint: "Use only the importance scale.",
  },
  {
    value: "simple_average",
    label: "Simple average of scales",
    hint: "Average of the importance and agreement weights.",
  },
];

export const CHOICE_FORMULAS = [
  {
    value: "option_weight",
    label: "Selected option weight",
    hint: "Uses the weight you set next to each option on the question.",
  },
  {
    value: "option_index",
    label: "Option order (1…n)",
    hint: "Score = position of the chosen option (1st = 1, 2nd = 2, …).",
  },
];

export const MULTI_FORMULAS = [
  {
    value: "sum_option_weights",
    label: "Sum of selected weights",
    hint: "Adds the weights of every option the participant selects.",
  },
  {
    value: "avg_option_weights",
    label: "Average of selected weights",
    hint: "Averages the weights of selected options.",
  },
  {
    value: "count_selected",
    label: "Count selected",
    hint: "Score = how many options were selected.",
  },
];

export const DEFAULT_SCORING_BY_TYPE = {
  rating: { formula: "star_value", include_in_aggregate: true },
  importance_agreement: {
    formula: "importance_x_agreement",
    include_in_aggregate: true,
  },
  open_ended: { formula: "none", include_in_aggregate: false },
  dropdown: { formula: "option_weight", include_in_aggregate: true },
  single_choice: { formula: "option_weight", include_in_aggregate: true },
  multiple_choice: { formula: "sum_option_weights", include_in_aggregate: true },
};

/** Map stored survey question → import/editor type key. */
export function questionToImportType(question) {
  if (!question) return "rating";
  if (question.question_type) {
    // Legacy: older "rating" meant dual-scale MCQ
    if (
      question.question_type === "rating" &&
      String(question.type || "").toLowerCase() === "mcq"
    ) {
      return "importance_agreement";
    }
    return question.question_type;
  }
  const t = String(question.type || "").toLowerCase();
  if (t === "text") return "open_ended";
  if (t === "rating") return "rating";
  if (t === "mcq") return "importance_agreement";
  if (t === "multi") return "multiple_choice";
  if (t === "dropdown") return "dropdown";
  const parts = question.parts || [];
  if (parts.some((p) => p.type === "rating")) return "rating";
  if (parts.some((p) => p.type === "agreement" || p.type === "importance")) {
    // Custom importance-only dropdowns are not dual-scale
    const hasAgreement = parts.some((p) => p.type === "agreement");
    const hasImportance = parts.some((p) => p.type === "importance");
    if (hasAgreement && hasImportance) return "importance_agreement";
    if (hasImportance && !hasAgreement) return "dropdown";
  }
  if (parts.some((p) => p.type === "text" || p.type === "textarea")) return "open_ended";
  if (parts.some((p) => p.type === "choice")) return "multiple_choice";
  return "rating";
}

export function optionsFromQuestion(question) {
  for (const part of question?.parts || []) {
    if (!Array.isArray(part.options) || !part.options.length) continue;
    const weights = part.option_weights || [];
    return part.options.map((o, i) => {
      const label = typeof o === "string" ? o : o?.label || "";
      const weight =
        typeof o === "object" && o?.weight != null
          ? o.weight
          : weights[i] != null
            ? weights[i]
            : i + 1;
      return { label, weight: String(weight) };
    }).filter((row) => row.label);
  }
  return [];
}

export function typeNeedsOptions(type) {
  return QUESTION_TYPES.find((t) => t.value === type)?.needsOptions === true;
}

export function emptyOptionRows() {
  return [
    { label: "", weight: "1" },
    { label: "", weight: "2" },
  ];
}
